import { TellentNavigationScope } from './client-navigation';
import { AppKind, TellentCurrentUser, TellentMember } from './types';

export type TellentToastCallback = (message: string) => void;

export enum TellentClientAuthErrorCode {
  BootstrapError = 'BootstrapError',
  OrgGuidUnavailable = 'OrgGuidUnavailable',
  OrgGuidMismatch = 'OrgGuidMismatch',
  TokenUnavailable = 'TokenUnavailable',
  TokenInvalid = 'TokenInvalid',
  UserUnavailable = 'UserUnavailable',
  MemberNotFoundInOrg = 'MemberNotFoundInOrg',
  MemberSeatNotFoundInOrg = 'MemberSeatNotFoundInOrg',
}

export type TellentClientBootstrapData = {
  member: TellentMember;
};

export type TellentClientBootstrapDataV2 = {
  member: TellentMember;
  accessToken: string;
  orgGuid: string;
  appGuid: string;
  appKind: `${AppKind}`;
  user: TellentCurrentUser;
  error: boolean;
  errorCode?: TellentClientAuthErrorCode;
  continueOnError?: boolean;
};

export type TellentClientLegacyAuthInitData = {
  accessToken: string;
  appGuid: string;
};

export type TellentClientAuthInitData = {
  accessToken: string;
  orgGuid: string;
  appGuid: string;
  appKind: `${AppKind}`;
  user: TellentCurrentUser;
  member: TellentMember;
  error: boolean;
  errorCode?: TellentClientAuthErrorCode;
  continueOnError?: boolean;
};

export type TellentClientInitData = TellentClientLegacyAuthInitData | TellentClientAuthInitData;

export type TellentClientConfig = {
  env: 'production' | 'rc' | 'staging' | 'local-staging' | 'local';
  navigationScope: TellentNavigationScope[];
  useOrgFallback?: boolean; // if set to true and no org guid is found in the url, the app will fallback to the first org guid found in user.members
  brand: 'kiwi' | 'javelo'; // Brand identifier, can be either 'kiwi' or 'javelo'
  toastCallbacks?: {
    success: TellentToastCallback; // Callback for success toasts
    error: TellentToastCallback; // Callback for error toasts
  };
  analyticsCallbacks?: {
    reset: () => void; // Callback for resetting analytics
    track: (name: string, props?: any, options?: any) => void; // Callback for tracking analytics events
    alias: (id: number, options?: any) => void; // Callback for aliasing analytics events
    identify: (userId?: number, traits?: Record<string, string | number>, options?: any) => void; // Callback for identifying analytics events
    page: (category: string, name: string, properties?: any, options?: any) => void; // Callback for page analytics events
  };
  ui?: {
    overlayContainerZIndex?: number;
    overlayZIndex?: number;
    overlayBackdropZIndex?: number;
  };
};

export type TellentClientDialogResult<O> = { success: true; payload: O } | undefined;

export interface EventEmitterLike<T> {
  emit: (e: T) => void;
}

export type InputSignalLike<T> = (v?: T) => T;

type PickProperties<C, P extends keyof C> =
  Pick<C, P> extends { [key in infer K]: any }
    ? {
        [key in K]: key extends keyof C
          ? C[key] extends EventEmitterLike<infer V>
            ? (e: CustomEvent<V>) => void
            : C[key] extends InputSignalLike<infer V>
              ? V
              : C[key]
          : never;
      }
    : never;

export type TellentWebComponent<C, P> = P extends keyof C ? PickProperties<C, P> : C;
